import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { CalculatorActions, readCalculatorQuery } from "@/components/calculator/CalculatorActions";
import { CalculatorFaqAccordion, HowToUseCalculator, RelatedCalculators } from "@/components/calculator/CalculatorEducation";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CLUSTER_META,
  CLUSTERS,
  type Cluster,
  type WeekPageData,
} from "@/lib/pregnancy/week-data";

function computeEDD(lmp: string): string | null {
  if (!lmp) return null;
  const d = new Date(lmp + "T00:00:00Z");
  if (isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + 280);
  return d.toISOString().slice(0, 10);
}
function weeksSince(lmp: string): number | null {
  if (!lmp) return null;
  const d = new Date(lmp + "T00:00:00Z");
  if (isNaN(d.getTime())) return null;
  const diff = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 7);
  return Math.max(0, Math.round(diff * 10) / 10);
}
function ivfEDD(retrieval: string, transferDay: 3 | 5): string | null {
  if (!retrieval) return null;
  const d = new Date(retrieval + "T00:00:00Z");
  if (isNaN(d.getTime())) return null;
  // gestational week 2 at retrieval; day-5 transfer = 2w+5d gestational at transfer
  // EDD = retrieval + (280 - 14) days = retrieval + 266 days
  d.setUTCDate(d.getUTCDate() + 266 - (transferDay === 3 ? 0 : 0));
  return d.toISOString().slice(0, 10);
}

export function PregnancyWeekPage({ data }: { data: WeekPageData }) {
  const { cluster, week, meta, fact, faqs } = data;

  const [lmp, setLmp] = useState("");
  const [retrieval, setRetrieval] = useState("");
  const [transferDay, setTransferDay] = useState<3 | 5>(5);

  useEffect(() => {
    const restored = readCalculatorQuery("preg", ["lmp", "retrieval", "transferDay"]);
    if (/^\d{4}-\d{2}-\d{2}$/.test(restored.lmp ?? "")) setLmp(restored.lmp);
    if (/^\d{4}-\d{2}-\d{2}$/.test(restored.retrieval ?? "")) setRetrieval(restored.retrieval);
    if (restored.transferDay === "3" || restored.transferDay === "5") setTransferDay(Number(restored.transferDay) as 3 | 5);
  }, []);

  const edd = cluster === "ivf" ? ivfEDD(retrieval, transferDay) : computeEDD(lmp);
  const gestWeeks = cluster === "ivf" ? null : weeksSince(lmp);
  const progressPct = Math.min(100, Math.round((week / 40) * 100));
  const trimester = fact.trimester;

  const resultText = edd
    ? `${CLUSTER_META[cluster].label}\nEstimated due date: ${edd}${gestWeeks !== null ? `\nCurrent gestational age: ${gestWeeks} weeks` : ""}`
    : "Enter a valid date to calculate an estimated due date.";

  return (
    <Layout>
      <article className="mx-auto max-w-3xl px-4 py-8">
        <nav className="text-xs text-muted-foreground mb-3" aria-label="Breadcrumb">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <span>{CLUSTER_META[cluster].label}</span> › Week {week}
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold leading-tight">{meta.h1}</h1>
        <p className="mt-3 text-muted-foreground">{data.intro}</p>

        {/* Progress */}
        <div className="mt-5 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between text-sm font-medium">
            <span>Trimester {trimester} · Week {week} of 40</span>
            <span className="tabular-nums text-primary">{progressPct}%</span>
          </div>
          <div className="mt-2 h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-center text-xs">
            <div><div className="text-muted-foreground">Baby size</div><div className="font-semibold">{fact.fruit}</div></div>
            <div><div className="text-muted-foreground">Length</div><div className="font-semibold tabular-nums">{fact.lengthCm} cm</div></div>
            <div><div className="text-muted-foreground">Weight</div><div className="font-semibold tabular-nums">{fact.weightG} g</div></div>
          </div>
        </div>

        {/* Calculator */}
        <section id="calculator" className="mt-6 rounded-2xl border border-primary/30 bg-card p-5">
          <h2 className="text-xl font-bold">{CLUSTER_META[cluster].label}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {cluster === "ivf"
              ? "Enter your egg retrieval date and select day-3 or day-5 transfer. EDD = retrieval + 266 days."
              : cluster === "conception"
              ? "Enter your LMP. Fetal age = gestational age − 2 weeks."
              : "Enter the first day of your last menstrual period (LMP). EDD = LMP + 280 days."}
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {cluster === "ivf" ? (
              <>
                <label className="text-sm">
                  <span className="mb-1 block font-medium">Egg retrieval date</span>
                  <input type="date" value={retrieval} onChange={(e) => setRetrieval(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2" />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block font-medium">Transfer day</span>
                  <select value={transferDay} onChange={(e) => setTransferDay(Number(e.target.value) as 3 | 5)} className="w-full rounded-lg border border-border bg-background px-3 py-2">
                    <option value={5}>Day 5 (Blastocyst)</option>
                    <option value={3}>Day 3 (Cleavage)</option>
                  </select>
                </label>
              </>
            ) : (
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block font-medium">Last menstrual period (LMP)</span>
                <input type="date" value={lmp} onChange={(e) => setLmp(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2" />
              </label>
            )}
          </div>
          <div className="mt-4 min-h-[116px] rounded-xl border border-border bg-muted/40 p-4 text-sm" style={{ contain: "layout" }} aria-live="polite">
          {edd ? (
            <>
              <div className="flex justify-between py-1">
                <span className="text-muted-foreground">Estimated due date (EDD)</span>
                <span className="font-bold text-primary tabular-nums">{edd}</span>
              </div>
              {gestWeeks !== null && (
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">Current gestational age</span>
                  <span className="font-semibold tabular-nums">{gestWeeks} weeks</span>
                </div>
              )}
              {cluster === "conception" && gestWeeks !== null && (
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">Fetal age (from conception)</span>
                  <span className="font-semibold tabular-nums">{Math.max(0, +(gestWeeks - 2).toFixed(1))} weeks</span>
                </div>
              )}
              {cluster === "countdown" && edd && (
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">Weeks left</span>
                  <span className="font-semibold tabular-nums">
                    {Math.max(0, Math.round(((new Date(edd).getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 7)) * 10) / 10)}
                  </span>
                </div>
              )}
            </>
          ) : <div className="flex min-h-[82px] items-center justify-center text-muted-foreground">Choose a valid date to see the estimated timeline.</div>}
          </div>
          <CalculatorActions queryPrefix="preg" values={{ lmp, retrieval, transferDay }} resultText={resultText} onReset={() => { setLmp(""); setRetrieval(""); setTransferDay(5); }} />
          <Tabs defaultValue="logic" className="mt-5">
            <TabsList className="grid w-full grid-cols-2"><TabsTrigger value="logic">Calculation logic</TabsTrigger><TabsTrigger value="limits">Important limits</TabsTrigger></TabsList>
            <TabsContent value="logic" className="rounded-lg border border-border bg-background p-4 text-sm leading-relaxed">
              {cluster === "ivf" ? `EDD = egg retrieval date + 266 days. Active transfer setting: day ${transferDay}.` : "EDD = first day of the last menstrual period + 280 days (40 weeks). Current gestational age is elapsed days divided by 7."}
            </TabsContent>
            <TabsContent value="limits" className="rounded-lg border border-border bg-background p-4 text-sm leading-relaxed text-muted-foreground">This is an estimate. Ultrasound dating and your clinician’s assessment may revise the due date, especially with irregular cycles or uncertain dates.</TabsContent>
          </Tabs>
        </section>

        <HowToUseCalculator inputLabel={cluster === "ivf" ? "Enter the retrieval date" : "Enter the LMP date"} resultLabel="Discuss the estimate with your clinician" />

        <section className="mt-10">
          <h2 className="text-xl font-bold">Practical Examples &amp; Use Cases</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead><tr>{["Starting date", "Method", "Calculation", "Planning use"].map((h) => <th key={h} className="border-b border-border px-2 py-2 text-left">{h}</th>)}</tr></thead>
              <tbody>
                <tr>{["1 Jan", cluster === "ivf" ? "Retrieval" : "LMP", cluster === "ivf" ? "+266 days" : "+280 days", "Estimated due date"].map((v) => <td key={v} className="border-b border-border/60 px-2 py-2">{v}</td>)}</tr>
                <tr>{["Known clinical date", "Ultrasound", "Clinician dating", "Confirm or revise estimate"].map((v) => <td key={v} className="border-b border-border/60 px-2 py-2">{v}</td>)}</tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Baby dev */}
        <section id="baby" className="mt-8">
          <h2 className="text-2xl font-bold">Baby at week {week}</h2>
          <p className="mt-2 text-foreground/90 leading-relaxed">{data.babyDev}</p>
        </section>

        {/* Mother body */}
        <section id="mother" className="mt-8">
          <h2 className="text-2xl font-bold">Mother's body — what changes</h2>
          <p className="mt-2 text-foreground/90 leading-relaxed">{data.motherBody}</p>
        </section>

        {/* Nutrition */}
        <section id="nutrition" className="mt-8">
          <h2 className="text-2xl font-bold">Nutrition & supplements</h2>
          <p className="mt-2 text-foreground/90 leading-relaxed">{data.nutrition}</p>
        </section>

        {/* Exercise */}
        <section id="exercise" className="mt-8">
          <h2 className="text-2xl font-bold">Safe exercise</h2>
          <p className="mt-2 text-foreground/90 leading-relaxed">{data.exercise}</p>
        </section>

        {/* Doctor visit checklist */}
        <section id="checklist" className="mt-8">
          <h2 className="text-2xl font-bold">Doctor visit checklist — week {week}</h2>
          <ul className="mt-2 space-y-2 text-sm">
            {data.doctorChecklist.map((c) => (
              <li key={c} className="flex gap-2"><span className="text-primary">✓</span><span>{c}</span></li>
            ))}
          </ul>
        </section>

        {/* Warning signs */}
        <section id="warning" className="mt-8 rounded-xl border border-destructive/40 bg-destructive/5 p-5">
          <h2 className="text-xl font-bold text-destructive">Warning signs — call your provider immediately</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {data.warningSigns.map((s) => (
              <li key={s} className="flex gap-2"><span>⚠</span><span>{s}</span></li>
            ))}
          </ul>
        </section>

        <CalculatorFaqAccordion faqs={faqs} title={`Week ${week} — Frequently asked questions`} />

        {/* E-E-A-T author + disclaimer */}
        <div className="mt-8 rounded-xl border border-border bg-muted/30 p-5 text-sm">
          <div className="font-semibold">Medically reviewed</div>
          <p className="text-muted-foreground">
            Content aligned with {data.authority}. Reviewed by Dr. Maria Rehman, MD (OB/GYN).
            This page is educational and does not replace personal medical advice — call your
            obstetrician for any concern.
          </p>
        </div>

        {/* Prev / Next */}
        <nav className="mt-8 flex flex-wrap items-center justify-between gap-3">
          {data.prevWeek ? (
            <a href={CLUSTER_META[cluster].routePattern(data.prevWeek)} className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted">
              ← Week {data.prevWeek}
            </a>
          ) : <span />}
          {data.nextWeek ? (
            <a href={CLUSTER_META[cluster].routePattern(data.nextWeek)} className="rounded-lg border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-medium hover:bg-primary/20">
              Week {data.nextWeek} →
            </a>
          ) : <span />}
        </nav>

        {/* Cross-cluster mesh */}
        <RelatedCalculators items={[
          ...CLUSTERS.filter((c) => c !== cluster).map((c) => ({ path: CLUSTER_META[c].routePattern(week), name: CLUSTER_META[c].label, description: `Week ${week} guidance and calculation for this pregnancy timeline.`, badge: `Week ${week}`, icon: "📅" })),
          ...(data.prevWeek ? [{ path: CLUSTER_META[cluster].routePattern(data.prevWeek), name: `${CLUSTER_META[cluster].label} — Week ${data.prevWeek}`, description: "Review the previous week in this pregnancy timeline.", badge: "Previous", icon: "←" }] : []),
          ...(data.nextWeek ? [{ path: CLUSTER_META[cluster].routePattern(data.nextWeek), name: `${CLUSTER_META[cluster].label} — Week ${data.nextWeek}`, description: "Continue to the next week in this pregnancy timeline.", badge: "Next", icon: "→" }] : []),
        ]} />
      </article>
    </Layout>
  );
}
